/**
 * MENU & PERMISSION CONFIGURATION
 *
 * Controls: sidebar menus, bypass rules, hide rules, route permissions.
 * Path permissions: use menu `module`/`submodule` + getPermissionKeyForPath (overrides for non-menu routes).
 *
 * USER TYPES:
 *   0 = Super Admin | 1 = Workspace Admin | 2 = Staff | 3 = Customer | 4 = Contact
 *   5 = Manager | 6 = Team Member
 *
 * Per menu item:
 *   bypass: [0, 1]          → show without permission check for these user types
 *   hide: [2]               → hide from these user types
 *   allowedUserTypes: [0,1] → show only to these user types
 */

import { SidebarIcons } from "../utils/icons";

// ── DASHBOARD MENU ────────────────────────────────────────────────────────────

export const DASHBOARD_MENU = [
  // 1. Dashboard
  {
    icon: SidebarIcons.DASHBOARD,
    path: "/",
    label: "Dashboard",
    type: "icon",
  },

  // 2. Manager Dashboard (only visible to team managers)
  {
    icon: SidebarIcons.STAFFATTENDANCE,
    type: "icon",
    path: "/hr/manager-dashboard",
    label: "Manager Dashboard",
    module: "hr",
    submodule: "attendance",
    hide: [0, 1],
  },

  // 3. HR Setup — foundational config + staff roster (must exist before leaves/payroll can be used)
  {
    icon: SidebarIcons.PROJECTS,
    path: null,
    label: "HR Setup",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.DEPARTMENT,
        type: "icon",
        path: "/configuration/manage-departments",
        label: "Departments",
        module: "configuration",
        submodule: "department",
      },
      {
        icon: SidebarIcons.DESIGNATION,
        type: "icon",
        path: "/configuration/manage-designation",
        label: "Designations",
        module: "configuration",
        submodule: "designation",
      },
      {
        icon: SidebarIcons.BRANCH,
        type: "icon",
        path: "/configuration/manage-branch",
        label: "Branches",
        module: "configuration",
        submodule: "branch",
      },
      {
        icon: SidebarIcons.TEAM,
        type: "icon",
        path: "/configuration/manage-team",
        label: "Teams",
        module: "hr",
        submodule: "team",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.HOLIDAY,
        type: "icon",
        path: "/configuration/leave-setup",
        label: "Leave Types",
        module: "configuration",
        submodule: "leave-type",
      },
      {
        icon: SidebarIcons.PERMISSSION,
        type: "icon",
        path: "/configuration/role",
        label: "Roles & Permissions",
        module: "configuration",
        submodule: "role",
      },
      {
        icon: SidebarIcons.STAFFDIRECTORY,
        type: "icon",
        path: "/hr/staff-directory",
        label: "Staff Directory",
        module: "hr",
        submodule: "user",
      },
      {
        icon: SidebarIcons.STAFFDIRECTORY,
        type: "icon",
        path: "/hr/onboarding",
        label: "Employee Onboarding",
        module: "hr",
        submodule: "user",
        hide: [1, 2, 3],
        bypass: [0],
      },
    ],
  },

  // 4. Recruitment — hire people (departments/designations must exist first)
  {
    icon: SidebarIcons.CLIENTS,
    path: null,
    label: "Recruitment",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.JOBOPENING,
        type: "icon",
        path: "/recruitment/job-opening",
        label: "Job Openings",
        module: "recruitment",
        submodule: "job-opening",
      },
      {
        icon: SidebarIcons.CANDIDATES,
        type: "icon",
        path: "/recruitment/candidates",
        label: "Candidates",
        module: "recruitment",
        submodule: "job-application",
      },
      {
        icon: SidebarIcons.CALENDAR,
        type: "icon",
        path: "/recruitment/interview-schedule",
        label: "Interview Schedule",
        module: "recruitment",
        submodule: "job-interview",
      },
    ],
  },

  // 5. Attendance & Time — daily operations
  {
    icon: SidebarIcons.STAFFATTENDANCE,
    path: null,
    label: "Attendance & Time",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.STAFFATTENDANCE,
        type: "icon",
        path: "/hr/staff-attendance",
        label: "Daily Attendance",
        module: "hr",
        submodule: "attendance",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.SALARYSETUP,
        type: "icon",
        path: "/hr/shift-editor",
        label: "Shift Management",
        module: "hr",
        submodule: "workspace-shift",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.STAFFATTENDANCE,
        type: "icon",
        path: "/hr/attendance-reports",
        label: "Staff Attendance Reports",
        module: "hr",
        submodule: "attendance",
        hide: [2],
        bypass: [0, 1],
      },
      // Staff-only attendance reports (merged from standalone item)
      {
        icon: SidebarIcons.STAFFATTENDANCE,
        type: "icon",
        path: "/staff/attendance-reports",
        label: "Attendance Reports",
        module: "self-employee",
        submodule: "self-attendance",
        hide: [0, 1, 3, 4],
      },
    ],
  },

  // 6. Leave Management
  {
    icon: SidebarIcons.STAFFLEAVE,
    path: null,
    label: "Leave Management",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.STAFFLEAVE,
        type: "icon",
        path: "/leaves/apply",
        label: "Leave requests",
      },
      {
        icon: SidebarIcons.STAFFLEAVE,
        type: "icon",
        path: "/hr/staff-leaves",
        label: "Leave Applications",
        module: "self-employee",
        submodule: "self-leave",
      },
      {
        icon: SidebarIcons.STAFFLEAVE,
        type: "icon",
        path: "/leaves/summary",
        label: "Leave Summary",
        module: "leave",
        submodule: "leave",
        bypass: [0, 1, 2],
      },
      {
        icon: SidebarIcons.HOLIDAY,
        type: "icon",
        path: "/hr/staff-holidays",
        label: "Holidays Calendar",
        module: "leave",
        submodule: "holiday",
        bypass: [0, 1],
      },
    ],
  },

  // 7. Payroll Management
  {
    icon: SidebarIcons.PAYROLLREPORT,
    path: null,
    label: "Payroll Management",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.PAYROLLREPORT,
        type: "icon",
        path: "/payroll/dashboard",
        label: "Payroll Dashboard",
        module: "payroll",
        submodule: "dashboard",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.SALARYSETUP,
        type: "icon",
        path: "/payroll/salary-structure",
        label: "Salary Structure",
        module: "payroll",
        submodule: "salary-structure",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.PAYROLLREPORT,
        type: "icon",
        path: "/hr/payslips",
        label: "Payslips",
        module: "payroll",
        submodule: "payslips",
        bypass: [0, 1, 2],
      },
    ],
  },

  // 8. Reimbursement
  {
    icon: SidebarIcons.PAYMENTS,
    path: null,
    label: "Reimbursement",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.PAYMENTS,
        type: "icon",
        path: "/reimbursement",
        label: "Reimbursement Dashboard",
        module: "reimbursement",
        submodule: "dashboard",
        bypass: [0, 1],
        hide: [2],
      },
      {
        icon: SidebarIcons.PAYMENTS,
        type: "icon",
        path: "/reimbursement/my-expenses",
        label: "My Expenses",
        module: "reimbursement",
        submodule: "my-expenses",
        bypass: [0, 1, 2],
      },
      {
        icon: SidebarIcons.PAYMENTS,
        type: "icon",
        path: "/reimbursement/manager-approvals",
        label: "Pending Approvals",
        module: "reimbursement",
        submodule: "dashboard",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.PAYMENTS,
        type: "icon",
        path: "/reimbursement/finance-queue",
        label: "Finance Queue",
        module: "reimbursement",
        submodule: "dashboard",
        bypass: [0, 1],
      },
      {
        icon: SidebarIcons.CONFIGRATION,
        type: "icon",
        path: "/reimbursement/categories",
        label: "Category Management",
        module: "reimbursement",
        submodule: "reimbursement-category",
        bypass: [0, 1],
      },
    ],
  },

  // 9. Reports & Analytics
  {
    icon: SidebarIcons.REPORTS,
    path: null,
    label: "Reports & Analytics",
    type: "icon",
    top: "true",
    subMenu: [
      {
        icon: SidebarIcons.REPORTS,
        type: "icon",
        path: "/reports",
        label: "Reports Dashboard",
        module: "reports",
        submodule: "dashboard",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
      {
        icon: SidebarIcons.PAYROLLREPORT,
        type: "icon",
        path: "/reports/payroll-cost",
        label: "Payroll Cost Report",
        module: "reports",
        submodule: "payroll",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
      {
        icon: SidebarIcons.STAFFATTENDANCE,
        type: "icon",
        path: "/reports/attendance-summary",
        label: "Attendance Summary",
        module: "reports",
        submodule: "attendance",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
      {
        icon: SidebarIcons.STAFFLEAVE,
        type: "icon",
        path: "/reports/leave-trends",
        label: "Leave Trends",
        module: "reports",
        submodule: "leave",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
      {
        icon: SidebarIcons.PAYMENTS,
        type: "icon",
        path: "/reports/reimbursement-spend",
        label: "Reimbursement Spend",
        module: "reports",
        submodule: "reimbursement",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
      {
        icon: SidebarIcons.HR,
        type: "icon",
        path: "/reports/headcount-attrition",
        label: "Headcount & Attrition",
        module: "reports",
        submodule: "headcount",
        bypass: [0, 1],
        hide: [2, 3, 4],
      },
    ],
  },

  // 11. Configuration (Admin only)
  {
    icon: SidebarIcons.CONFIGRATION,
    type: "icon",
    label: "Configuration",
    allowedUserTypes: [0, 1],
    subMenu: [
      {
        icon: SidebarIcons.DEPARTMENT,
        type: "icon",
        path: "/configuration/general-setup",
        label: "General Setup",
        module: "configuration",
        submodule: "workspace",
      },
    ],
  },
];

// ── SETTINGS MENU ─────────────────────────────────────────────────────────────

export const SETTINGS_MENU = [
  {
    icon: SidebarIcons.CALENDAR,
    type: "icon",
    path: "/calendar",
    label: "Calendar",
    module: "calender",
    submodule: "reminder",
    hide: [3],
    bypass: [0, 1, 2],
  },
];

// ── PATH ↔ PERMISSION (menu is primary; no duplicate rows for sidebar paths) ──
//
// - Menu items with `module` + `submodule` define permissions for that path and child
//   paths (longest prefix wins).
// - PATH_PERMISSION_OVERRIDES: routes not in the menu (payroll drill-downs, legacy /hr/*, etc.).
// - getPathPermissionPatterns(): dynamic segments or ambiguous prefixes.

function walkMenuPermissionRows(menuList, acc = []) {
  for (const item of menuList) {
    if (item.path && item.module && item.submodule) {
      acc.push({ path: item.path, module: item.module, submodule: item.submodule });
    }
    if (item.subMenu?.length) walkMenuPermissionRows(item.subMenu, acc);
  }
  return acc;
}

let _menuPermissionRowsCache = null;
function getMenuPermissionRows() {
  if (!_menuPermissionRowsCache) {
    _menuPermissionRowsCache = walkMenuPermissionRows([...DASHBOARD_MENU, ...SETTINGS_MENU]);
  }
  return _menuPermissionRowsCache;
}

/** Longest-prefix match on menu paths that carry module/submodule (child routes inherit). */
export const getMenuPermissionByLongestPrefix = (fullPath) => {
  if (!fullPath) return null;
  const rows = getMenuPermissionRows();
  let best = null;
  for (const row of rows) {
    if (fullPath === row.path || fullPath.startsWith(`${row.path}/`)) {
      if (!best || row.path.length > best.path.length) best = row;
    }
  }
  return best ? { module: best.module, submodule: best.submodule } : null;
};

/** Exact paths not in the menu tree (or legacy aliases). */
export const PATH_PERMISSION_OVERRIDES = {
  "/configuration/loan-cateories": { module: "configuration", submodule: "loan-type" },
  "/configuration": { module: "configuration", submodule: "workspace" },

  "/payroll/manage-allowance": { module: "payroll", submodule: "allowance" },
  "/payroll/manage-deduction": { module: "payroll", submodule: "deduction" },
  "/payroll/employee-salary": { module: "payroll", submodule: "manage-salary" },
  "/payroll/generate-salary": { module: "payroll", submodule: "generate-salary" },
  "/payroll/payout-reports": { module: "payroll", submodule: "payout-reports" },
  "/payroll/statutory-reports": { module: "payroll", submodule: "statutory-reports" },
  "/payroll/salary-report": { module: "payroll", submodule: "salary-report" },

  "/hr/manage-allowance": { module: "payroll", submodule: "allowance" },
  "/hr/manage-deduction": { module: "payroll", submodule: "deduction" },
  "/hr/manage-salary-structure": { module: "payroll", submodule: "salary-structure" },
  "/hr/salary-setup": { module: "payroll", submodule: "manage-salary" },
  "/hr/generate-salary": { module: "payroll", submodule: "generate-salary" },
  "/hr/payroll-reports": { module: "payroll", submodule: "payout-reports" },
  "/hr/payroll-dashboard": { module: "payroll", submodule: "dashboard" },
  "/hr/statutory-reports": { module: "payroll", submodule: "statutory-reports" },

  "/staff/payroll": { module: "payroll", submodule: "salary-report" },
  "/staff/staff-holidays": { module: "hr", submodule: "holiday" },

  "/leads": { module: "finance", submodule: "lead" },
  "/application": { module: "finance", submodule: "application" },
  "/task": { module: "finance", submodule: "task" },
  "/customers": { module: "customer", submodule: "customer" },
  "/photos": { module: "gallery", submodule: "folder" },
};

function getPathPermissionPatterns() {
  const reportPaths = new Set(
    getMenuPermissionRows()
      .filter((r) => r.path.startsWith("/reports"))
      .map((r) => r.path)
  );
  return [
    {
      test: (p) => /^\/reimbursement\/expenses\/[^/]+$/.test(p),
      permission: { module: "reimbursement", submodule: "reimbursement" },
    },
    {
      test: (p) => /^\/payroll\/payroll-runs\/[^/]+$/.test(p),
      permission: { module: "payroll", submodule: "payroll" },
    },
    {
      test: (p) => /^\/payroll\/payslips(\/[^/]+)?$/.test(p),
      permission: { module: "payroll", submodule: "payslips" },
    },
    {
      test: (p) => /^\/hr\/payroll-runs\/[^/]+$/.test(p),
      permission: { module: "hr", submodule: "payroll" },
    },
    {
      test: (p) => /^\/hr\/payslips(\/[^/]+)?$/.test(p),
      permission: { module: "payroll", submodule: "payslips" },
    },
    {
      test: (p) => /^\/reports\/[^/]+$/.test(p) && !reportPaths.has(p),
      permission: { module: "reports", submodule: "view" },
    },
  ];
}

/**
 * Resolves `{ module, submodule }` for a URL. Menu rows are the main source; overrides
 * and patterns cover non-menu and dynamic routes.
 */
export const getPermissionKeyForPath = (path) => {
  if (!path) return null;
  if (PATH_PERMISSION_OVERRIDES[path]) return PATH_PERMISSION_OVERRIDES[path];
  for (const { test, permission } of getPathPermissionPatterns()) {
    if (test(path)) return permission;
  }
  return getMenuPermissionByLongestPrefix(path);
};

/**
 * Back-compat for `PATH_PERMISSION_MAP[path]` — resolves via getPermissionKeyForPath (not a static duplicate of menu).
 */
export const PATH_PERMISSION_MAP = new Proxy(
  {},
  {
    get: (_, prop) => (typeof prop === "string" ? getPermissionKeyForPath(prop) : undefined),
    has: (_, prop) => typeof prop === "string" && getPermissionKeyForPath(prop) != null,
  }
);

// ── HELPER FUNCTIONS ──────────────────────────────────────────────────────────

export const getMenuConfigByPath = (
  path,
  menuList = [...DASHBOARD_MENU, ...SETTINGS_MENU]
) => {
  for (const menu of menuList) {
    if (menu.path === path) return menu;
    if (menu.subMenu) {
      const sub = menu.subMenu.find((s) => s.path === path);
      if (sub) return sub;
    }
  }

  for (const menu of menuList) {
    if (menu.path && path.startsWith(menu.path)) return menu;
    if (menu.subMenu) {
      for (const sub of menu.subMenu) {
        if (sub.path && path.startsWith(sub.path)) return sub;
      }
    }
  }

  return null;
};

export const shouldBypassPermissionForPath = (path, userType) => {
  if (!path || userType === undefined || userType === null) return false;
  const t = Number(userType);
  return getMenuConfigByPath(path)?.bypass?.includes(Number.isFinite(t) ? t : userType) || false;
};

export const shouldHideMenuForPath = (path, userType) => {
  if (!path || userType === undefined || userType === null) return false;
  const t = Number(userType);
  return getMenuConfigByPath(path)?.hide?.includes(Number.isFinite(t) ? t : userType) || false;
};

export const shouldBypassPermissionForModule = (module, submodule, userType) => {
  if (!module || !submodule || userType === undefined || userType === null) return false;
  const t = Number(userType);
  const normalizedUserType = Number.isFinite(t) ? t : userType;

  const findInMenu = (menuList) => {
    for (const menu of menuList) {
      if (menu.module === module && menu.submodule === submodule) {
        return menu.bypass?.includes(normalizedUserType) || false;
      }
      if (menu.subMenu) {
        const found = findInMenu(menu.subMenu);
        if (found) return found;
      }
    }
    return false;
  };

  return findInMenu([...DASHBOARD_MENU, ...SETTINGS_MENU]);
};

export const shouldHideMenuForModule = (module, submodule, userType) => {
  if (!module || !submodule || userType === undefined || userType === null) return false;
  const t = Number(userType);
  const normalizedUserType = Number.isFinite(t) ? t : userType;

  const findInMenu = (menuList) => {
    for (const menu of menuList) {
      if (menu.module === module && menu.submodule === submodule) {
        return menu.hide?.includes(normalizedUserType) || false;
      }
      if (menu.subMenu) {
        const found = findInMenu(menu.subMenu);
        if (found !== false) return found;
      }
    }
    return false;
  };

  return findInMenu([...DASHBOARD_MENU, ...SETTINGS_MENU]);
};

export const getAllowedUserTypesForPath = (path) => {
  if (!path) return [];
  return getMenuConfigByPath(path)?.bypass || [];
};

export const getAllowedUserTypesForModule = (module, submodule) => {
  if (!module || !submodule) return [];

  const findInMenu = (menuList) => {
    for (const menu of menuList) {
      if (menu.module === module && menu.submodule === submodule) {
        return menu.allowedUserTypes || [];
      }
      if (menu.subMenu) {
        const found = findInMenu(menu.subMenu);
        if (found.length > 0) return found;
      }
    }
    return [];
  };

  return findInMenu([...DASHBOARD_MENU, ...SETTINGS_MENU]);
};

// ── FILTER MENU BY PERMISSIONS ────────────────────────────────────────────────

export const filterMenuByPermissions = (menu, userPermissions, userType = null, user = null) => {
  if (!menu?.length) return menu || [];

  const t = userType === undefined || userType === null ? userType : Number(userType);
  const normalizedUserType = Number.isFinite(t) ? t : userType;

  const isAdmin = normalizedUserType === 0 || normalizedUserType === 1;
  const isTeamManager = user?.is_team_manager === true || user?.is_team_manager === 1;

  // Workspace Admin (type 1): no permission checks; only apply hide rules.
  if (normalizedUserType === 1) {
    const isHidden = (item) => {
      if (normalizedUserType !== null) {
        if (item.path && shouldHideMenuForPath(item.path, normalizedUserType)) return true;
        if (item.hide?.includes(normalizedUserType)) return true;
      }
      return false;
    };

    return menu
      .map((item) => {
        if (isHidden(item)) return null;
        if (item.subMenu?.length > 0) {
          const filteredSubMenu = item.subMenu.filter((sub) => !isHidden(sub));
          return filteredSubMenu.length > 0 ? { ...item, subMenu: filteredSubMenu } : null;
        }
        return item;
      })
      .filter(Boolean);
  }

  if (isAdmin) {
    return menu
      .map((item) =>
        item.path === "/hr/manager-dashboard" ? (isTeamManager ? item : null) : item
      )
      .filter(Boolean);
  }

  const isItemVisible = (item) => {
    if (item.path === "/hr/manager-dashboard") return isTeamManager;

    if (normalizedUserType !== null) {
      if (item.path && shouldHideMenuForPath(item.path, normalizedUserType)) return false;
      if (item.hide?.includes(normalizedUserType)) return false;
    }

    if (item.allowedUserTypes?.length > 0) return item.allowedUserTypes.includes(normalizedUserType);

    if (normalizedUserType !== null) {
      if (item.bypass?.includes(normalizedUserType)) return true;
      if (item.path && shouldBypassPermissionForPath(item.path, normalizedUserType)) return true;
      if (item.module && item.submodule && shouldBypassPermissionForModule(item.module, item.submodule, normalizedUserType)) return true;
    }

    if (item.module && item.submodule) {
      if (!userPermissions) return false;
      const modulePerms = userPermissions[item.module];
      if (!modulePerms) return false;
      const submodulePerms = modulePerms[item.submodule] || [];
      return submodulePerms.includes(`${item.submodule}-view`);
    }

    if (item.path) {
      const permissionKey = getPermissionKeyForPath(item.path);
      if (!permissionKey) return true;

      const { module, submodule } = permissionKey;
      if (normalizedUserType !== null && shouldBypassPermissionForModule(module, submodule, normalizedUserType)) return true;
      if (!userPermissions) return false;
      const modulePerms = userPermissions[module];
      if (!modulePerms) return false;
      return (modulePerms[submodule] || []).includes(`${submodule}-view`);
    }

    return true;
  };

  return menu
    .map((item) => {
      if (item.path === "/hr/manager-dashboard") return isTeamManager ? item : null;

      if (normalizedUserType !== null) {
        if (item.path && shouldHideMenuForPath(item.path, normalizedUserType)) return null;
        if (item.hide?.includes(normalizedUserType)) return null;
      }

      if (item.allowedUserTypes?.length > 0 && !item.allowedUserTypes.includes(normalizedUserType)) return null;

      if (item.subMenu?.length > 0) {
        const filteredSubMenu = item.subMenu.filter((sub) =>
          sub.isGroupTitle ? true : isItemVisible(sub)
        );
        const hasValidSubItems = filteredSubMenu.some((sub) => !sub.isGroupTitle);
        return hasValidSubItems ? { ...item, subMenu: filteredSubMenu } : null;
      }

      return isItemVisible(item) ? item : null;
    })
    .filter(Boolean);
};
